import type { LocationQuery } from 'vue-router'
import type { CustomModuleBaseTypeEnum } from '~/shared/api'
import {
  ROUTE_QUERY_IS_PROJECT_REQUEST,
  ROUTE_QUERY_IS_PROJECT_REQUEST_ACTIVE,
} from './projectRequestRoute'

export const ROUTE_QUERY_LANDING_BUTTON_SLOT = 'landingButtonSlot'

export const ROUTE_QUERY_LANDING_EDIT_RETURN = 'landingEditReturn'

export const ROUTE_QUERY_LANDING_TEMPLATE_ID = 'landingTemplateId'

export const ROUTE_QUERY_LANDING_BUTTON_MODE = 'landingButtonMode'

export const ROUTE_QUERY_LANDING_ENTITY_ID = 'landingEntityId'

export const ROUTE_QUERY_LANDING_MODULE_TYPE = 'landingModuleType'

export const LANDING_BUTTON_MODE_MODULE = 'module'

export const LANDING_BUTTON_MODE_MODULE_OBJECT = 'moduleObject'

const LANDING_TEMPLATE_FLOW_QUERY_KEYS = [
  ROUTE_QUERY_LANDING_BUTTON_SLOT,
  ROUTE_QUERY_LANDING_EDIT_RETURN,
  ROUTE_QUERY_LANDING_TEMPLATE_ID,
  ROUTE_QUERY_LANDING_BUTTON_MODE,
  ROUTE_QUERY_LANDING_ENTITY_ID,
] as const

export function hasLandingTemplateFlowQuery(query: LocationQuery): boolean {
  return LANDING_TEMPLATE_FLOW_QUERY_KEYS.some((key) => {
    const raw = query[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    return typeof value === 'string' && value.length > 0
  })
}

export function omitLandingTemplateFlowQuery(
  query: LocationQuery,
): Record<string, string> {
  const result: Record<string, string> = {}

  for (const [key, raw] of Object.entries(query)) {
    if (LANDING_TEMPLATE_FLOW_QUERY_KEYS.includes(key as typeof LANDING_TEMPLATE_FLOW_QUERY_KEYS[number])) {
      continue
    }

    const value = Array.isArray(raw) ? raw[0] : raw
    if (typeof value === 'string' && value.length > 0) {
      result[key] = value
    }
  }

  return result
}

export function getLandingButtonModeFromQuery(query: LocationQuery): string {
  const raw = query[ROUTE_QUERY_LANDING_BUTTON_MODE]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : LANDING_BUTTON_MODE_MODULE
}

function getExplicitLandingButtonModeFromQuery(query: LocationQuery): string | null {
  const raw = query[ROUTE_QUERY_LANDING_BUTTON_MODE]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : null
}

export function isLandingModuleObjectSelectMode(query: LocationQuery): boolean {
  return getLandingButtonModeFromQuery(query) === LANDING_BUTTON_MODE_MODULE_OBJECT
}

export function getLandingButtonSlotFromQuery(query: LocationQuery): string {
  const raw = query[ROUTE_QUERY_LANDING_BUTTON_SLOT]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : ''
}

export function getLandingEditReturnFromQuery(query: LocationQuery): string {
  const raw = query[ROUTE_QUERY_LANDING_EDIT_RETURN]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : ''
}

export function getLandingTemplateIdFromQuery(query: LocationQuery): string {
  const raw = query[ROUTE_QUERY_LANDING_TEMPLATE_ID]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : ''
}

export function getLandingModuleTypeFromQuery(
  query: LocationQuery,
): CustomModuleBaseTypeEnum | undefined {
  const raw = query[ROUTE_QUERY_LANDING_MODULE_TYPE]
  const value = Array.isArray(raw) ? raw[0] : raw
  if (typeof value !== 'string' || !value.length) {
    return undefined
  }

  return value as CustomModuleBaseTypeEnum
}

export function getLandingEntityIdFromQuery(query: LocationQuery): string {
  const raw = query[ROUTE_QUERY_LANDING_ENTITY_ID]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : ''
}

export function resolveLandingFlowEntityId(
  params: Record<string, string | string[] | undefined>,
  query: LocationQuery,
): string {
  const fromQuery = getLandingEntityIdFromQuery(query)
  if (fromQuery) {
    return fromQuery
  }

  const rawId = params.id
  const id = Array.isArray(rawId) ? rawId[0] : rawId
  return typeof id === 'string' ? id : ''
}

export function buildLandingTemplateFlowQuery(
  query: LocationQuery,
  extras?: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {}

  const buttonSlot = getLandingButtonSlotFromQuery(query)
  if (buttonSlot) {
    result[ROUTE_QUERY_LANDING_BUTTON_SLOT] = buttonSlot
  }

  const editReturn = getLandingEditReturnFromQuery(query)
  if (editReturn) {
    result[ROUTE_QUERY_LANDING_EDIT_RETURN] = editReturn
  }

  const templateId = getLandingTemplateIdFromQuery(query)
  if (templateId) {
    result[ROUTE_QUERY_LANDING_TEMPLATE_ID] = templateId
  }

  const buttonMode = getExplicitLandingButtonModeFromQuery(query)
  if (buttonMode) {
    result[ROUTE_QUERY_LANDING_BUTTON_MODE] = buttonMode
  }

  const entityId = getLandingEntityIdFromQuery(query)
  if (entityId) {
    result[ROUTE_QUERY_LANDING_ENTITY_ID] = entityId
  }

  const isProjectRequest = query[ROUTE_QUERY_IS_PROJECT_REQUEST]
  if (
    isProjectRequest === ROUTE_QUERY_IS_PROJECT_REQUEST_ACTIVE
    || isProjectRequest === 'true'
  ) {
    result[ROUTE_QUERY_IS_PROJECT_REQUEST] = ROUTE_QUERY_IS_PROJECT_REQUEST_ACTIVE
  }

  if (extras) {
    Object.assign(result, extras)
  }

  if (!result[ROUTE_QUERY_LANDING_BUTTON_MODE]) {
    result[ROUTE_QUERY_LANDING_BUTTON_MODE] = LANDING_BUTTON_MODE_MODULE
  }

  return result
}
